import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SendIcon from '@mui/icons-material/Send';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchThresholds, updateThresholds, testAlert, type AlertThresholds } from '../api/alerts';
import { useSnackbar } from '../context/SnackbarContext';

// ── Alert Thresholds Panel ─────────────────────────────────────────────────

function AlertThresholdsPanel() {
  const showSnackbar = useSnackbar();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Partial<AlertThresholds>>({});
  const [testing, setTesting] = useState<string | null>(null);

  const { data: thresholds, isLoading } = useQuery({
    queryKey: ['alerts/thresholds'],
    queryFn: fetchThresholds,
    staleTime: 60_000,
  });

  const saveMut = useMutation({
    mutationFn: updateThresholds,
    onSuccess: (updated) => {
      qc.setQueryData(['alerts/thresholds'], updated);
      setDraft({});
      showSnackbar('Thresholds saved', 'success');
    },
    onError: () => showSnackbar('Failed to save thresholds', 'error'),
  });

  const handleTest = async (metric: 'disk' | 'ram' | 'load') => {
    setTesting(metric);
    try {
      await testAlert(metric);
      showSnackbar(`Test alert sent for ${metric}`, 'success');
    } catch {
      showSnackbar('Failed to send test alert', 'error');
    } finally {
      setTesting(null);
    }
  };

  const merged = thresholds ? { ...thresholds, ...draft } : null;

  const field = (
    key: keyof AlertThresholds,
    label: string,
    unit: string,
    min: number,
    max: number,
    step = 1,
  ) => (
    <TextField
      key={key}
      label={label}
      type="number"
      size="small"
      value={merged?.[key] ?? ''}
      onChange={(e) => setDraft((d) => ({ ...d, [key]: Number(e.target.value) }))}
      disabled={isLoading}
      inputProps={{ min, max, step }}
      InputProps={{ endAdornment: <InputAdornment position="end">{unit}</InputAdornment> }}
      sx={{ width: 180 }}
    />
  );

  return (
    <Paper sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <NotificationsActiveIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: '0.65rem', fontWeight: 600 }}>
          Resource Alert Thresholds
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Stack>

      <Stack spacing={2.5}>
        <Box>
          <Typography variant="body2" fontWeight={600} mb={1}>Disk</Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            {field('disk_percent', 'Threshold', '%', 1, 100)}
            {field('disk_duration_min', 'Duration', 'min', 1, 1440)}
            <Button size="small" variant="outlined" startIcon={<SendIcon fontSize="small" />}
              onClick={() => void handleTest('disk')} disabled={testing !== null}>
              {testing === 'disk' ? 'Sending…' : 'Test'}
            </Button>
          </Stack>
        </Box>

        <Box>
          <Typography variant="body2" fontWeight={600} mb={1}>RAM</Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            {field('ram_percent', 'Threshold', '%', 1, 100)}
            {field('ram_duration_min', 'Duration', 'min', 1, 1440)}
            <Button size="small" variant="outlined" startIcon={<SendIcon fontSize="small" />}
              onClick={() => void handleTest('ram')} disabled={testing !== null}>
              {testing === 'ram' ? 'Sending…' : 'Test'}
            </Button>
          </Stack>
        </Box>

        <Box>
          <Typography variant="body2" fontWeight={600} mb={1}>Load Average</Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            {field('load_multiplier', 'Multiplier', '× cores', 0.1, 32, 0.1)}
            {field('load_duration_min', 'Duration', 'min', 1, 1440)}
            <Button size="small" variant="outlined" startIcon={<SendIcon fontSize="small" />}
              onClick={() => void handleTest('load')} disabled={testing !== null}>
              {testing === 'load' ? 'Sending…' : 'Test'}
            </Button>
          </Stack>
        </Box>

        <Box>
          <Typography variant="body2" fontWeight={600} mb={1}>Global</Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {field('hysteresis_min', 'Hysteresis', 'min', 1, 1440)}
            {field('throttle_min', 'Throttle', 'min', 1, 1440)}
          </Stack>
        </Box>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" mt={2.5}>
        <Button
          variant="contained"
          disabled={Object.keys(draft).length === 0 || saveMut.isPending}
          onClick={() => saveMut.mutate(draft)}
        >
          {saveMut.isPending ? 'Saving…' : 'Save'}
        </Button>
      </Stack>
    </Paper>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function ResourceAlert() {
  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Resource Alerts</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure thresholds for disk, RAM, and load average alerts
          </Typography>
        </Box>
      </Stack>

      <AlertThresholdsPanel />
    </Box>
  );
}
